/**
 * Human-readable names for `{{trigger.x}}`/`{{context.x}}` template tokens, so
 * the UI never shows raw template syntax to the user. Both namespaces resolve
 * to the same thing at runtime (WorkflowRunnerService's resolveTemplateValue
 * — `context` is what every real node default in nodeCatalog.ts actually
 * uses; `trigger` is an older/alternate alias) and share the same key set, so
 * one label map covers both. The underlying stored token is unchanged; only
 * the DISPLAY changes.
 *
 * Known keys get a specific name; anything else falls back to a generic
 * "the triggering <thing>" derived from the key itself, so a future trigger
 * variable (e.g. an eventual "emailId") reads sensibly with no code change.
 */
const KNOWN_TRIGGER_VARIABLES: Record<string, string> = {
  meetingId: "the triggering meeting",
  clientId: "the triggering client",
};

const TRIGGER_TOKEN = /^\{\{(?:trigger|context)\.(\w+)\}\}$/;

function fallbackLabel(key: string): string {
  const withoutId = key.replace(/Id$/, "");
  const spaced = withoutId.replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase();
  return `the triggering ${spaced || key}`;
}

/** e.g. "{{trigger.meetingId}}" -> "the triggering meeting". Returns the input unchanged if it isn't a trigger token. */
export function describeTriggerToken(token: string): string {
  const match = TRIGGER_TOKEN.exec(token.trim());
  if (!match) return token;
  const key = match[1];
  return KNOWN_TRIGGER_VARIABLES[key] || fallbackLabel(key);
}

export function isTriggerToken(token: string): boolean {
  return TRIGGER_TOKEN.test(token.trim());
}

/** Scans a workflow's nodes for every distinct `{{trigger.x}}`/`{{context.x}}` token actually in use, and describes them together — e.g. "the triggering meeting and the triggering client". Used for the toolbar's "Test variables" caption instead of raw template syntax. */
export function describeUsedTriggerVariables(nodes: Array<{ config: unknown }>): string {
  const found = new Set<string>();
  const allTokens = /\{\{(?:trigger|context)\.\w+\}\}/g;
  for (const node of nodes) {
    const matches = JSON.stringify(node.config).match(allTokens);
    matches?.forEach((m) => found.add(m));
  }
  if (!found.size) return "the triggering meeting/client";
  const labels = [...found].map((token) => describeTriggerToken(token).replace(/^the /, ""));
  return labels.length === 1 ? `the ${labels[0]}` : `the ${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}
